import { AppContext, OnChangeParams } from '@juvare/platform-app/App';
import { SettingColumns, SettingData } from '@juvare/platform-sdk-react/backend/features/editable-settings/shared/entityTypes';
import { AppState } from '@juvare/platform-sdk-react/types';
import { Filter, FilterIdSubFilter } from '@juvare/platform-shared/features/storage';
import { ExtendedAccountColumns } from '../../../contact-management/entities/extended-account/types';
import { getExtendedAccountStorage } from '../../../contact-management/utils/getExtendedAccountStorage';
import { BackgroundCheckConfig } from '../../defaults';
import { recalculateBackgroundCheckStatus } from '../../entities/background-check/utils';
import {
    BackgroundCheckColumns,
    backgroundCheckEntityName,
    getEnabledBackgroundCheckTypes,
    TypedBackgroundCheckColumns,
} from '../../shared';
import { BackgroundCheckStatus, BackgroundCheckType } from '../../types';
import { getBackgroundCheckStorage, isBackgroundCheckConfigUpdated } from '../../utils';

export function onChangedAsync(context: AppContext<AppState>, params: OnChangeParams<SettingData, SettingColumns>) {
    const backgroundCheckStorage = getBackgroundCheckStorage(context);
    const accountStorage = getExtendedAccountStorage(context);

    if (params.action === 'update') {
        const affectedBackgroundCheckTypes = isBackgroundCheckConfigUpdated(params.prevData, params.data);

        if (affectedBackgroundCheckTypes?.length > 0) {
            const config: BackgroundCheckConfig | undefined = params.data?.value ? JSON.parse(params.data.value) : undefined;

            if (!config) {
                return;
            }

            console.log('Starting backgroundCheck record creation/update after backgroundCheckConfig update....');

            const enabledTypes: BackgroundCheckType[] = getEnabledBackgroundCheckTypes(config).filter((type) =>
                affectedBackgroundCheckTypes.includes(type),
            );

            const enabledTypesSubFilter: Filter<ExtendedAccountColumns>[] = enabledTypes.map((enabledType) => ({
                op: 'not-in',
                idSubFilter: {
                    entity: backgroundCheckEntityName,
                    column: 'accountId',
                    filter: [
                        // We only need to address ones that are enabled
                        {
                            column: 'type',
                            op: '=',
                            value: enabledType,
                        },
                    ],
                } satisfies FilterIdSubFilter<BackgroundCheckColumns>['idSubFilter'],
            }));

            const accountsMissingBackgroundChecks = accountStorage.list({
                filter: [
                    // Ignore deleted accounts
                    {
                        column: 'systemStatus',
                        op: '!=',
                        value: 'deleted',
                    },
                    {
                        op: 'or',
                        filter: enabledTypesSubFilter,
                    },
                ],
            }).rows;

            console.log(
                `${accountsMissingBackgroundChecks.length} accounts to have background checks recalculated of types `,
                enabledTypes,
            );

            const accountIdsMissingBackgroundChecks = accountsMissingBackgroundChecks?.map(({ id: accountId }) => accountId);

            Object.keys(config.type).forEach((type) => {
                if (!config.type[type as BackgroundCheckType]?.enabled) {
                    return;
                }

                // Check if record exists
                const existingBackgroundChecksByType = backgroundCheckStorage.list({
                    filter: [
                        {
                            column: 'type',
                            op: '=',
                            value: type,
                        },
                        {
                            column: 'accountId',
                            op: 'in',
                            value: accountIdsMissingBackgroundChecks,
                        },
                    ],
                }).rows;

                // Create new records if they do not exist
                accountsMissingBackgroundChecks.forEach((account) => {
                    const accountId = account.id;

                    // Create new records
                    const alreadyHasRecord = existingBackgroundChecksByType.some(
                        (backgroundCheck) => backgroundCheck.columns.accountId === accountId,
                    );

                    if (!alreadyHasRecord) {
                        const accountResponse = accountStorage.get(accountId);

                        const backgroundCheckToBeCreated: TypedBackgroundCheckColumns = {
                            requestedBy: 'System',
                            accountId,
                            status: 'readyToSubmit', // Does not matter what status we give here as it won't be used as previous status
                            type: type as BackgroundCheckType,
                            updatedAt: Date.now(),
                            consent: Boolean(account?.columns?.consentBackgroundCheck),
                        };

                        const {
                            backgroundCheck: { status, updatedAt, requestedBy },
                        } = recalculateBackgroundCheckStatus(context, {
                            backgroundCheck: backgroundCheckToBeCreated,
                            accountData: accountResponse.data,
                            skipHistoryCreation: true,
                        });

                        backgroundCheckStorage.post({
                            action: 'create',
                            data: {
                                requestedBy: requestedBy,
                                accountId: accountId,
                                status: status,
                                type: type as BackgroundCheckType,
                                updatedAt: updatedAt,
                                consent: Boolean(account?.columns?.consentBackgroundCheck),
                            },
                        });
                    }
                });

                // Recalculate affected BC records
                const affectedRecords = backgroundCheckStorage.list({
                    filter: [
                        {
                            column: 'type',
                            op: 'in',
                            value: affectedBackgroundCheckTypes,
                        },
                    ],
                });

                affectedRecords.rows.forEach((bcRecord) => {
                    const oldStatus = bcRecord.columns.status as BackgroundCheckStatus;
                    const type = bcRecord.columns.type as BackgroundCheckType;
                    const accountId = bcRecord.columns.accountId;
                    const consent = bcRecord.columns.consent;
                    const oldUpdatedAt = bcRecord.columns.updatedAt;

                    const accountRecord = accountStorage.get(bcRecord.columns.accountId);

                    const newBCRecord: TypedBackgroundCheckColumns = {
                        requestedBy: bcRecord.columns.requestedBy,
                        type,
                        accountId,
                        status: oldStatus,
                        consent: Boolean(accountRecord.columns.consentBackgroundCheck),
                        updatedAt: oldUpdatedAt,
                    };

                    const {
                        backgroundCheck: { status, updatedAt },
                    } = recalculateBackgroundCheckStatus(context, {
                        backgroundCheck: newBCRecord,
                        accountData: accountRecord.data,
                        skipHistoryCreation: false,
                    });

                    const statusChanged = oldStatus !== status;
                    const consentChanged = consent !== Boolean(accountRecord.data.consentBackgroundCheck);

                    if (statusChanged || consentChanged) {
                        backgroundCheckStorage.post({
                            action: 'update',
                            id: bcRecord.id,
                            data: {
                                requestedBy: 'System',
                                accountId: accountId,
                                type: type,
                                status: status,
                                updatedAt: updatedAt,
                                consent: Boolean(accountRecord.columns.consentBackgroundCheck),
                            },
                        });
                    }
                });
            });

            console.log('Finished backgroundCheck record creation/update after Background Check Config update.');
        }
    }
}
